<template>
    <transition name="cz3__fade">
        <div
            v-if="showModal"
            @click.self="emitClose"
            class="z-[70] fixed top-0 left-0 w-full h-full flex flex-col justify-center items-center bg-black bg-opacity-30"
        >
            <div class="shadow-md w-[296px] overflow-y-auto bg-white text-ink">
                <div class="flex justify-between items-start px-[20px] pt-[27px] bg-gray-F0F0F0">
                    <div class="flex flex-col">
                        <div class="text-[16px] font-semibold">
                            {{ $t('SHARE_DESIGN') }}
                        </div>

                        <div class="text-[12px] -mt-[3px] font-oceanside font-semibold normal-case text-gray-dark">
                            {{ productName }}
                        </div>
                    </div>
                    <close-button @click="emitClose" />
                </div>

                <div class="flex flex-col">
                    <div class="bg-gray-F0F0F0 border-b border-gray-DFDFDF py-[8px] px-[4px]" v-if="!!lastRenderedImage?.src">
                        <img
                            :src="lastRenderedImage?.src"
                            class="h-[170px] w-full object-contain"
                        >
                    </div>
                    <ul class="px-[20px]">
                        <li class="flex flex-col justify-center border-b border-gray-DFDFDF px-[5px] sm:p-5 h-[56px]">
                            <share-modal-button
                                @click="copyLink"
                                :icon-name="linkCopied ? 'black-check' : 'link'"
                                :disabled="!canCopyLink"
                                class="w-full font-semibold"
                            >
                                <span v-if="!linkCopied">
                                    {{ $t('COPY') }}
                                </span>

                                <span v-else>
                                    {{ $t('COPIED') }}
                                </span>
                            </share-modal-button>
                        </li>

                        <!--
                        <li class="flex flex-col justify-center border-b border-gray-DFDFDF px-[5px] sm:p-5 h-[56px]">
                            <share-modal-button
                                @click="openEmailModal"
                                icon-name="email"
                                class="w-full font-semibold capitalize"
                            >
                                {{ $t('EMAIL') }}
                            </share-modal-button>
                        </li>
                        -->

                        <li class="flex flex-col justify-center border-b border-gray-DFDFDF px-[5px] sm:p-5 h-[56px]">
                            <share-modal-button
                                @click="handlePrint"
                                icon-name="print"
                                class="w-full font-semibold"
                            >
                                {{ $t('PRINT') }}
                            </share-modal-button>
                        </li>

                        <!--
                        <li v-if="canAddToWishlist" class="flex flex-col justify-center px-[5px] sm:p-5 h-[56px]">
                            <share-modal-button
                                @click="handleAddToWishlist"
                                icon-name="heart"
                                class="w-full font-semibold"
                            >
                                {{ $t('ADD_TO_WISHLIST') }}
                            </share-modal-button>
                        </li>
                        -->
                    </ul>
                </div>
            </div>

            <share-modal-email
                @modalClose="handleEmailModalClose"
                :show-modal="showEmailModal"
            />
        </div>
    </transition>
</template>

<script>
    import allStores from '../stores/all';

    import CloseButton from './buttons/CloseButton.vue';
    import ShareModalButton from './buttons/ShareModalButton.vue';
    import ShareModalEmail from './ShareModalEmail.vue';

    export default {
        mixins: [allStores],

        components: {
            CloseButton,
            ShareModalButton,
            ShareModalEmail,
        },

        props: {
            showModal: {
                type: Boolean,
                default: false,
            },
            modalImage: {
                type: String,
                default: '',
            },
        },

        data() {
            return {
                showEmailModal: false,
                recipe: null,
                sharePreparationPromise: null,
            };
        },

        computed: {
            productName() {
                return this.customizerStore.productName;
            },

            linkCopied() {
                return this.appStore.linkCopied;
            },

            isPrintMode() {
                return this.appStore.isPrintMode;
            },

            lastRenderedImage() {
                return this.customizerStore.lastRenderedImage;
            },

            totalSteps() {
                return this.customizerStore.totalSteps;
            },

            totalSelectionsMade() {
                return this.customizerStore.totalSelectionsMade;
            },

            canAddToWishlist() {
                if (this.inErrorState) {
                    return false;
                }

                return this.totalSelectionsMade === this.totalSteps;
            },

            canCopyLink() {
                return !!this.recipe && !this.sharePreparationPromise;
            },

            inErrorState() {
                return false;
            },
        },

        watch: {
            showModal() {
                if (this.showModal) {
                    this.recipe = null;
                    this.sharePreparationPromise = null;
                    this.prepareShare().catch(() => {
                        this.recipe = null;
                    });
                }
            },
        },

        methods: {
            prepareShare() {
                if (this.sharePreparationPromise) {
                    return this.sharePreparationPromise;
                }

                this.sharePreparationPromise = this.customizerStore.beforeShare().then((recipe) => {
                    this.recipe = recipe;
                    return recipe;
                }).finally(() => {
                    this.sharePreparationPromise = null;
                });

                return this.sharePreparationPromise;
            },

            async ensureShareReady() {
                if (this.sharePreparationPromise) {
                    return this.sharePreparationPromise;
                }

                if (this.recipe) {
                    return this.recipe;
                }

                return this.prepareShare();
            },

            async copyLink() {
                const recipe = await this.ensureShareReady();

                const url = new URL(window.location.href);

                url.searchParams.set('recipe', recipe.recipe.id);

                try {
                    await navigator.clipboard.writeText(url.toString());
                } catch (e) {
                    this.appStore.setLinkCopied(false);
                    return;
                }

                this.appStore.setLinkCopied(true);

                await new Promise((resolve) => { setTimeout(resolve, 2000); });

                this.appStore.setLinkCopied(false);
            },

            openEmailModal() {
                this.showEmailModal = true;
            },

            async handlePrint() {
                this.emitClose();
                await this.$nextTick();
                this.appStore.setIsPrintMode(true);

                let hasFinished = false;
                let fallbackTimeout = null;
                let finishPrint = null;

                const handleReturnToPage = () => {
                    if (fallbackTimeout) {
                        clearTimeout(fallbackTimeout);
                    }

                    fallbackTimeout = setTimeout(finishPrint, 250);
                };

                const handleVisibilityChange = () => {
                    if (document.visibilityState === 'visible') {
                        handleReturnToPage();
                    }
                };

                finishPrint = () => {
                    if (hasFinished) {
                        return;
                    }

                    hasFinished = true;
                    this.appStore.setIsPrintMode(false);
                    window.removeEventListener('afterprint', finishPrint);
                    window.removeEventListener('focus', handleReturnToPage);
                    window.removeEventListener('pageshow', handleReturnToPage);
                    document.removeEventListener('visibilitychange', handleVisibilityChange);

                    if (fallbackTimeout) {
                        clearTimeout(fallbackTimeout);
                        fallbackTimeout = null;
                    }
                };

                window.addEventListener('afterprint', finishPrint);
                window.addEventListener('focus', handleReturnToPage);
                window.addEventListener('pageshow', handleReturnToPage);
                document.addEventListener('visibilitychange', handleVisibilityChange);

                await this.$nextTick();

                setTimeout(() => { // allow time for non-print elements to be hidden
                    window.print();
                }, 1000);
            },

            emitClose() {
                this.appStore.setLinkCopied(false);

                this.$emit('modalClose');
            },

            addToCart() {
                this.customizerStore.addToCart();
            },

            handleAddToWishlist() {
                this.emitClose();

                this.customizerStore.addToWishlist();
            },

            handleEmailModalClose() {
                this.showEmailModal = false;
                this.emitClose();
            },
        },
    };
</script>
